import { supabase } from '../lib/supabase';

class InventoryService {
    // ── MATERIALS ─────────────────────────────────────────────
    async getMaterials() {
        const { data, error } = await supabase
            .from('materials')
            .select('*')
            .neq('category', 'Archived')
            .order('name');
        if (error) throw error;
        return data;
    }

    async addMaterial({ name, sku, unit, minStockLevel, initialStock, machineId, userId }) {
        if (!name || !sku) throw new Error('Name and SKU are required.');

        // 1. Create the material
        const { data: material, error } = await supabase
            .from('materials')
            .insert([{
                name: name.trim(),
                sku: sku.trim().toUpperCase(),
                unit: unit || 'KG',
                min_stock_level: 0,
                current_stock: parseFloat(initialStock) || 0
            }])
            .select()
            .single();

        if (error) {
            if (error.code === '23505') throw new Error(`Type "${sku}" already exists.`);
            throw error;
        }

        // 2. Log the initial stock if provided
        if (parseFloat(initialStock) > 0) {
            await supabase
                .from('inbound_logs')
                .insert([{
                    material_id: material.id,
                    quantity: parseFloat(initialStock),
                    unit: unit || 'KG',
                    batch_source: 'Initial Stock',
                    user_id: userId,
                    machine_id: machineId || 'Warehouse'
                }]);
        }

        return material;
    }

    async deleteMaterial(id) {
        const { error } = await supabase
            .from('materials')
            .delete()
            .eq('id', id);
        
        if (error) {
            // PostgREST error codes: 23503 is foreign_key_violation
            // Also check for status 409 (Conflict) which Supabase might return direct
            if (error.code === '23503' || error.status === 409 || (error.message && error.message.toLowerCase().includes('conflict'))) {
                const err = new Error('Conflict: Material has history');
                err.code = 'CONFLICT';
                throw err;
            }
            throw error;
        }
    }

    async archiveMaterial(id) {
        const { error } = await supabase
            .from('materials')
            .update({ category: 'Archived' })
            .eq('id', id);
        if (error) throw error;
    }

    // ── INBOUND STOCK ─────────────────────────────────────────
    async logInbound(materialId, quantity, unit, source, userId, machineId) {
        const qty = parseFloat(quantity);
        if (isNaN(qty) || qty <= 0) throw new Error('Invalid quantity.');

        // 1. Get material to update stock
        const { data: material, error: mError } = await supabase
            .from('materials')
            .select('current_stock')
            .eq('id', materialId)
            .single();
        if (mError) throw mError;

        // 2. Update material stock
        const { error: uError } = await supabase
            .from('materials')
            .update({ current_stock: (parseFloat(material.current_stock) || 0) + qty })
            .eq('id', materialId);
        if (uError) throw uError;

        // 3. Create log
        const { data: log, error: lError } = await supabase
            .from('inbound_logs')
            .insert([{
                material_id: materialId,
                quantity: qty,
                unit: unit,
                batch_source: source || '',
                user_id: userId,
                machine_id: machineId || 'Warehouse'
            }])
            .select()
            .single();
        if (lError) throw lError;

        return log;
    }

    async deleteInboundLog(logId) {
        // 1. Get the log entry first
        const { data: log, error: fError } = await supabase
            .from('inbound_logs')
            .select('material_id, quantity')
            .eq('id', logId)
            .single();
        if (fError) throw fError;

        // 2. Get current material stock
        const { data: material, error: mError } = await supabase
            .from('materials')
            .select('current_stock')
            .eq('id', log.material_id)
            .single();
        if (mError) throw mError;

        // 3. Update material stock (reversing the add)
        const oldStock = parseFloat(material.current_stock) || 0;
        const logQty = parseFloat(log.quantity) || 0;
        const newStock = Math.max(0, oldStock - logQty);

        const { error: uError } = await supabase
            .from('materials')
            .update({ current_stock: newStock })
            .eq('id', log.material_id);
        if (uError) throw uError;

        // 4. Delete the log
        const { error: dError } = await supabase
            .from('inbound_logs')
            .delete()
            .eq('id', logId);
        if (dError) throw dError;
    }

    // ── STOCK OUT BATCHES ────────────────────────────────────
    async createStockOutBatch(machineId, shiftType, userId) {
        const { data, error } = await supabase
            .from('production_batches')
            .insert([{
                machine_id: machineId,
                shift_type: shiftType,
                status: 'Open',
                user_id: userId
            }])
            .select()
            .single();
        if (error) throw error;

        // Return batch with empty arrays for compatibility
        return { ...data, inputs: [], outputs: [], waste: [] };
    }

    async deleteBatch(batchId) {
        // Cascade delete will handle inputs, outputs, and waste logs via schema constraints
        const { error } = await supabase
            .from('production_batches')
            .delete()
            .eq('id', batchId);
        if (error) throw error;
    }

    async adminDeleteBatch(batchId) {
        // 1. Get all inputs for this batch to restore stock
        const { data: inputs, error: iError } = await supabase
            .from('production_inputs')
            .select('material_id, quantity_used')
            .eq('batch_id', batchId);
        if (iError) throw iError;

        // 2. Restore stock for each input
        for (const input of (inputs || [])) {
            const { data: material, error: mError } = await supabase
                .from('materials')
                .select('current_stock')
                .eq('id', input.material_id)
                .single();
            
            if (!mError && material) {
                const newStock = (parseFloat(material.current_stock) || 0) + (parseFloat(input.quantity_used) || 0);
                await supabase
                    .from('materials')
                    .update({ current_stock: newStock })
                    .eq('id', input.material_id);
            }
        }

        // 3. Delete the batch (cascade handles the rest)
        return this.deleteBatch(batchId);
    }

    async getBatch(batchId) {
        const { data, error } = await supabase
            .from('production_batches')
            .select(`
                *,
                production_inputs:production_inputs(id, material_id, quantity_used, materials(name, sku)),
                production_outputs:production_outputs(id, product_name, quantity_produced, unit, rolls, size),
                waste_logs:waste_logs(id, waste_type, quantity)
            `)
            .eq('id', batchId)
            .maybeSingle();

        if (error) throw error;
        if (!data) {
            const err = new Error('Batch not found');
            err.code = 'NOT_FOUND';
            throw err;
        }

        // Map Supabase response to expected frontend structure
        return {
            ...data,
            inputs: data.production_inputs.map(i => ({
                id: i.id,
                materialId: i.material_id,
                materialName: i.materials.name,
                materialSku: i.materials.sku,
                quantity: i.quantity_used
            })),
            outputs: data.production_outputs.map(o => ({
                id: o.id,
                productName: o.product_name,
                quantity: o.quantity_produced,
                unit: o.unit,
                rolls: o.rolls,
                size: o.size
            })),
            waste: data.waste_logs.map(w => ({
                id: w.id,
                type: w.waste_type,
                quantity: w.quantity
            }))
        };
    }

    async addStockOutInput(batchId, materialId, quantity) {
        const qty = parseFloat(quantity);
        if (isNaN(qty) || qty <= 0) throw new Error('Invalid quantity.');

        // 1. Get material to deduct stock
        const { data: material, error: mError } = await supabase
            .from('materials')
            .select('current_stock')
            .eq('id', materialId)
            .single();
        if (mError) throw mError;

        // 2. Deduct stock
        const newStock = Math.max(0, (parseFloat(material.current_stock) || 0) - qty);
        const { error: uError } = await supabase
            .from('materials')
            .update({ current_stock: newStock })
            .eq('id', materialId);
        if (uError) throw uError;

        // 3. Log input
        const { error: iError } = await supabase
            .from('production_inputs')
            .insert([{
                batch_id: batchId,
                material_id: materialId,
                quantity_used: qty
            }]);
        if (iError) throw iError;
    }

    async addStockOutOutput(batchId, productName, quantity, unit = 'KG', rolls = 0, size = null) {
        const qty = parseFloat(quantity);
        if (isNaN(qty) || qty <= 0) throw new Error('Invalid quantity.');

        const { error } = await supabase
            .from('production_outputs')
            .insert([{
                batch_id: batchId,
                product_name: productName.trim(),
                quantity_produced: qty,
                unit: unit,
                rolls: parseInt(rolls) || 0,
                size: size
            }]);
        if (error) throw error;
    }

    async addWaste(batchId, type, quantity) {
        const qty = parseFloat(quantity);
        if (isNaN(qty) || qty <= 0) throw new Error('Invalid quantity.');

        const { error } = await supabase
            .from('waste_logs')
            .insert([{
                batch_id: batchId,
                waste_type: type,
                quantity: qty
            }]);
        if (error) throw error;
    }

    async reconcileBatch(batchId) {
        const batch = await this.getBatch(batchId);

        const totalInput = batch.inputs.reduce((s, i) => s + i.quantity, 0);
        const totalOutput = batch.outputs.reduce((s, o) => s + o.quantity, 0);
        const totalWaste = batch.waste.reduce((s, w) => s + w.quantity, 0);

        const diff = Math.abs(totalInput - (totalOutput + totalWaste));
        const status = 'Closed';

        const { error } = await supabase
            .from('production_batches')
            .update({
                status,
                end_time: new Date().toISOString()
            })
            .eq('id', batchId);
        if (error) throw error;

        return { totalInput, totalOutput, totalWaste, discrepancy: diff };
    }

    async removeStockOutInput(inputId) {
        // 1. Get the input to restore stock
        const { data: input, error: fError } = await supabase
            .from('production_inputs')
            .select('material_id, quantity_used')
            .eq('id', inputId)
            .single();
        if (fError) throw fError;

        // 2. Restore stock
        const { data: material, error: mError } = await supabase
            .from('materials')
            .select('current_stock')
            .eq('id', input.material_id)
            .single();
        
        if (!mError && material) {
            const newStock = (parseFloat(material.current_stock) || 0) + (parseFloat(input.quantity_used) || 0);
            await supabase
                .from('materials')
                .update({ current_stock: newStock })
                .eq('id', input.material_id);
        }

        // 3. Delete record
        const { error: dError } = await supabase
            .from('production_inputs')
            .delete()
            .eq('id', inputId);
        if (dError) throw dError;
    }

    async removeStockOutOutput(outputId) {
        const { error } = await supabase
            .from('production_outputs')
            .delete()
            .eq('id', outputId);
        if (error) throw error;
    }

    async removeWaste(wasteId) {
        const { error } = await supabase
            .from('waste_logs')
            .delete()
            .eq('id', wasteId);
        if (error) throw error;
    }

    // ── ANALYTICS ─────────────────────────────────────────────
    async getStockOutBatches() {
        // Needed for history and multi-view consistency
        const { data, error } = await supabase
            .from('production_batches')
            .select(`
                *,
                app_users(username),
                production_inputs:production_inputs(
                    quantity_used,
                    materials(name, sku)
                )
            `)
            .order('start_time', { ascending: false });
        if (error) throw error;

        // Map table names back to common view names
        return data.map(b => ({
            ...b,
            startTime: b.start_time,
            machineId: ((b.machine_id && b.machine_id.trim().toLowerCase() === 'a') || (b.machine_id && b.machine_id.trim().toLowerCase() === 'machine a')) ? 'Machine A' : 
                       ((b.machine_id && b.machine_id.trim().toLowerCase() === 'b') || (b.machine_id && b.machine_id.trim().toLowerCase() === 'machine b')) ? 'Machine B' : 
                       (b.machine_id ? b.machine_id.trim() : 'Warehouse'),
            shiftType: b.shift_type,
            userName: b.app_users?.username || 'System',
            inputs: (b.production_inputs || []).map(i => ({
                materialName: i.materials?.name || 'Unknown',
                materialSku: i.materials?.sku || '—',
                quantity: i.quantity_used
            }))
        }));
    }

    async getMaterialUsage() {
        // Fetches all inputs joined with batches (for date/machine filtering) and materials (for names)
        const { data, error } = await supabase
            .from('production_inputs')
            .select(`
                id,
                material_id,
                quantity_used,
                logged_at,
                materials(name, sku),
                production_batches(machine_id, start_time)
            `)
            .order('logged_at', { ascending: false });
        if (error) throw error;
        
        return data.map(row => ({
            id: row.id,
            materialId: row.material_id,
            materialName: row.materials?.name,
            materialSku: row.materials?.sku,
            quantity: row.quantity_used,
            timestamp: row.logged_at,
            machineId: ((row.production_batches?.machine_id && row.production_batches?.machine_id.trim().toLowerCase() === 'a') || (row.production_batches?.machine_id && row.production_batches?.machine_id.trim().toLowerCase() === 'machine a')) ? 'Machine A' : 
                       ((row.production_batches?.machine_id && row.production_batches?.machine_id.trim().toLowerCase() === 'b') || (row.production_batches?.machine_id && row.production_batches?.machine_id.trim().toLowerCase() === 'machine b')) ? 'Machine B' : 
                       (row.production_batches?.machine_id ? row.production_batches?.machine_id.trim() : 'Warehouse'),
            batchStartTime: row.production_batches?.start_time
        }));
    }

    async getEfficiency() {
        const { data, error } = await supabase
            .from('production_batches')
            .select(`
                status,
                production_inputs(quantity_used),
                waste_logs(quantity)
            `)
            .eq('status', 'Closed');
        if (error) throw error;
        if (data.length === 0) return null;

        let totalInputAll = 0;
        let totalWasteAll = 0;

        data.forEach(batch => {
            batch.production_inputs.forEach(i => totalInputAll += parseFloat(i.quantity_used));
            batch.waste_logs.forEach(w => totalWasteAll += parseFloat(w.quantity));
        });

        if (totalInputAll === 0) return null;
        return (((totalInputAll - totalWasteAll) / totalInputAll) * 100).toFixed(1);
    }

    async getWasteByMachine() {
        const { data, error } = await supabase
            .from('production_batches')
            .select(`
                machine_id,
                production_inputs(quantity_used),
                waste_logs(quantity)
            `);
        if (error) throw error;

        const map = {};
        data.forEach(batch => {
            const mid = batch.machine_id;
            if (!map[mid]) map[mid] = { input: 0, waste: 0 };
            batch.production_inputs.forEach(i => map[mid].input += parseFloat(i.quantity_used));
            batch.waste_logs.forEach(w => map[mid].waste += parseFloat(w.quantity));
        });

        return Object.entries(map).map(([machine, { input, waste }]) => ({
            machine,
            wasteRatio: input > 0 ? ((waste / input) * 100).toFixed(1) : '0.0',
        }));
    }

    async getInboundLogs() {
        const { data, error } = await supabase
            .from('inbound_logs')
            .select(`
                *,
                materials(name, sku),
                app_users(username)
            `)
            .order('received_at', { ascending: false });
        if (error) throw error;

        return data.map(l => ({
            id: l.id,
            materialId: l.material_id,
            materialName: l.materials?.name,
            materialSku: l.materials?.sku,
            quantity: l.quantity,
            unit: l.unit,
            source: l.batch_source,
            timestamp: l.received_at,
            userName: l.app_users?.username || 'System',
            machineId: ((l.machine_id && l.machine_id.trim().toLowerCase() === 'a') || (l.machine_id && l.machine_id.trim().toLowerCase() === 'machine a')) ? 'Machine A' : 
                       ((l.machine_id && l.machine_id.trim().toLowerCase() === 'b') || (l.machine_id && l.machine_id.trim().toLowerCase() === 'machine b')) ? 'Machine B' : 
                       (l.machine_id ? l.machine_id.trim() : 'Warehouse')
        }));
    }

    async getTotalInboundByMaterial() {
        const logs = await this.getInboundLogs();
        const map = {};
        logs.forEach(l => {
            map[l.materialName] = (map[l.materialName] || 0) + parseFloat(l.quantity);
        });
        return map;
    }

    async getWasteLogs() {
        const { data, error } = await supabase
            .from('waste_logs')
            .select(`
                *,
                production_batches(machine_id)
            `)
            .order('logged_at', { ascending: false });
        if (error) throw error;
        return data.map(w => ({
            ...w,
            timestamp: w.logged_at,
            type: w.waste_type,
            batchId: w.batch_id,
            machineId: w.production_batches?.machine_id
        }));
    }

    async getProductionOutputs() {
        const { data, error } = await supabase
            .from('production_outputs')
            .select(`
                *,
                unit,
                production_batches(machine_id, start_time)
            `)
            .order('logged_at', { ascending: false });
        if (error) throw error;
        
        return data.map(row => ({
            ...row,
            timestamp: row.logged_at,
            machineId: row.production_batches?.machine_id,
            batchStartTime: row.production_batches?.start_time,
            rolls: row.rolls,
            size: row.size
        }));
    }

    // ── AUTH & USERS ──────────────────────────────────────────
    async authenticate(username, password) {
        if (!username || !password) throw new Error('Username and password are required.');

        const { data, error } = await supabase
            .from('app_users')
            .select('id, username, role, machine_id, shift_type, phone, is_phone_verified, created_at')
            .eq('username', username.trim().toLowerCase())
            .eq('password', password)
            .single();

        if (error || !data) throw new Error('Invalid username or password.');
        return data;
    }

    async checkUserPhone(identifier) {
        if (!identifier) throw new Error('Identifier (Username or Phone) is required.');
        const cleanId = identifier.trim().toLowerCase();
        
        let query = supabase.from('app_users').select('username, phone, is_phone_verified');
        
        // Match numbers, plus, spaces, etc. to detect a phone vs a text username
        if (/^[+\d\s]+$/.test(cleanId) && cleanId.replace(/\D/g, '').length >= 9) {
            const normalizedPhone = this._normalizePhone(cleanId);
            query = query.eq('phone', normalizedPhone);
        } else {
            query = query.eq('username', cleanId);
        }

        const { data, error } = await query.single();

        if (error || !data) throw new Error('Account not found.');
        return data; // { username: "...", phone: "...", is_phone_verified: true/false }
    }

    async isUserUnique(username, phone) {
        if (!username || !phone) throw new Error('Username and phone are required.');
        const cleanPhone = phone.replace(/\s/g, '');
        
        // Normalize 07... to +254... for consistency during check
        const normalizedPhone = cleanPhone.startsWith('0') && cleanPhone.length === 10
            ? '+254' + cleanPhone.slice(1)
            : cleanPhone;

        const { data, error } = await supabase
            .from('app_users')
            .select('username, phone')
            .or(`username.eq.${username.trim().toLowerCase()},phone.eq.${normalizedPhone}`);

        if (error) throw error;
        
        if (data && data.length > 0) {
            const match = data[0];
            if (match.username.toLowerCase() === username.trim().toLowerCase()) {
                throw new Error('Username already taken.');
            }
            throw new Error('Phone number already associated with an account.');
        }
        return true;
    }

    _normalizePhone(phone) {
        let phoneStr = String(phone || '').replace(/\s/g, '');
        // Local Kenya format (07...)
        if (phoneStr.startsWith('0') && phoneStr.length === 10) {
            return '+254' + phoneStr.slice(1);
        }
        // Kenya format without + (2547...)
        if (phoneStr.startsWith('254') && phoneStr.length === 12) {
            return '+' + phoneStr;
        }
        // International E.164 (already +...)
        if (phoneStr.startsWith('+')) {
            return phoneStr;
        }
        // Default: Add + if missing and it looks like a valid international number starts with 1 or 2...
        if (phoneStr.length >= 10 && !phoneStr.startsWith('+')) {
            return '+' + phoneStr;
        }
        return phoneStr;
    }

    async sendOTP(phone) {
        // Detect environment: Electron vs Web (PWA)
        const isElectron = !!(window.require && window.require('electron'));
        const normalizedPhone = this._normalizePhone(phone);
        
        if (isElectron) {
            console.log('Sending OTP via Electron IPC...');
            const { ipcRenderer } = window.require('electron');
            return await ipcRenderer.invoke('send-otp', { phone: normalizedPhone });
        } else {
            console.log('Sending OTP via Supabase Edge Function [otp-handler]...');
            // For Web/PWA, call Supabase Edge Function
            const { data, error } = await supabase.functions.invoke('otp-handler', {
                body: { phone: normalizedPhone, action: 'send' }
            });
            
            if (error) {
                console.error('Supabase Function Error [raw]:', error);
                let detail = error.message;
                
                try {
                    // FunctionsHttpError often includes the context (Response object)
                    if (error.context && typeof error.context.text === 'function') {
                        // Clone the response if possible to avoid body already read issues
                        const response = error.context.clone ? error.context.clone() : error.context;
                        const text = await response.text();
                        console.log('Supabase Function Error [body]:', text);
                        try {
                            const json = JSON.parse(text);
                            detail = json.message || json.error || text;
                        } catch (e) {
                            detail = text;
                        }
                    }
                } catch (e) {
                    console.error('Fallback error extraction failed:', e);
                }
                
                throw new Error(`Cloud SMS Error: ${detail}`);
            }
            return data;
        }
    }

    async verifyOTP(phone, code) {
        const isElectron = !!(window.require && window.require('electron'));
        const normalizedPhone = this._normalizePhone(phone);

        if (isElectron) {
            console.log('Verifying OTP via Electron IPC...');
            const { ipcRenderer } = window.require('electron');
            const result = await ipcRenderer.invoke('verify-otp', { phone: normalizedPhone, code });
            console.log('IPC verify-otp response:', result);
            return result.status === 'success' || result.status === 'approved';
        } else {
            console.log('Verifying OTP via Supabase Edge Function [otp-handler]...');
            // For Web/PWA, call Supabase Edge Function
            const { data, error } = await supabase.functions.invoke('otp-handler', {
                body: { phone: normalizedPhone, code, action: 'verify' }
            });
            console.log('Supabase verify-otp response:', data);

            if (error) {
                console.error('Supabase Function Error [verify]:', error);
                throw new Error(`Verification failed: ${error.message || 'Network error'}`);
            }
            return data.status === 'success' || data.status === 'approved';
        }
    }

    async resetPassword(username, newPassword) {
        if (!username || !newPassword) throw new Error('Username and new password are required.');
        if (newPassword.length < 8) throw new Error('Password must be at least 8 characters.');

        const { data, error } = await supabase
            .from('app_users')
            .update({ password: newPassword })
            .eq('username', username.trim().toLowerCase())
            .select('id')
            .single();

        if (error) throw error;
        if (!data) throw new Error('User not found.');
        return data;
    }

    async getUsers() {
        const { data, error } = await supabase
            .from('app_users')
            .select('id, username, role, machine_id, shift_type, phone, is_phone_verified, created_at')
            .order('username');
        if (error) throw error;
        return data;
    }

    async updateUser(userId, updates) {
        const { data, error } = await supabase
            .from('app_users')
            .update(updates)
            .eq('id', userId)
            .select('id, username, role, machine_id, shift_type, phone, is_phone_verified, created_at')
            .single();
        if (error) throw error;
        return data;
    }

    async registerUser(username, password, phone, role = 'operator') {
        const { data, error } = await supabase
            .from('app_users')
            .insert([{
                username: username.trim().toLowerCase(),
                password,
                phone: phone ? phone.trim() : null,
                role
            }])
            .select('id, username, role, machine_id, shift_type, phone, is_phone_verified, created_at')
            .single();
        if (error) {
            if (error.code === '23505') throw new Error('Username or phone already exists.');
            throw error;
        }
        return data;
    }

    async resetData() {
        // Danger zone: Delete all rows
        const tables = ['waste_logs', 'production_outputs', 'production_inputs', 'production_batches', 'inbound_logs', 'materials', 'app_users'];
        for (const table of tables) {
            const { error } = await supabase.from(table).delete().neq('id', '00000000-0000-0000-0000-000000000000');
            if (error) console.error(`Error resetting ${table}:`, error);
        }
    }
}

export const inventoryService = new InventoryService();
